import React, { useState } from 'react';
import { MdDashboard, MdEmail, MdLock, MdSecurity } from "react-icons/md";
import { FaEye, FaRegClipboard, FaHome, FaUserCog, FaChartBar, FaBriefcase, FaUsers, FaFileInvoiceDollar, FaGlobe } from "react-icons/fa";
import { FiLogOut } from "react-icons/fi";
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { usePermission } from '../../../hooks/usePermission';

import Forms from "./Forms";
import HomeContent from "./HomeContent";
import VisitsTable from "./VisitsTable";
import Analytics from "./Analytics";
import Projects from "./Projects";
import TeamPerformance from "./TeamPerformance";
import Invoices from "./Invoices";
import AdminManagement from "./AdminManagement";
import WebsiteContent from "./WebsiteContent";
import { SERVER } from '../../../config/config';
import Navbar from '../../../components/Navbar';

const Dashboard = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { can, isSuperAdmin } = usePermission();

  const handleLogout = async () => {
      await logout();
      navigate('/admin/login');
  };

  // Define all possible sections with their required permission
  const allSections = [
    { id: 'dashboard', label: 'Dashboard', icon: <MdDashboard />, permission: null }, // Always visible
    { id: 'analytics', label: 'Analytics', icon: <FaChartBar />, permission: 'analytics.view' },
    { id: 'projects', label: 'Projects', icon: <FaBriefcase />, permission: 'projects.view' },
    { id: 'team', label: 'Team Performance', icon: <FaUsers />, permission: 'team_performance.view' },
    { id: 'invoices', label: 'Invoices & Payments', icon: <FaFileInvoiceDollar />, permission: 'invoices.view' },
    { id: 'website-content', label: 'Website Content', icon: <FaGlobe />, permission: 'content.view' },
    { id: 'visits', label: 'Visit Analytics', icon: <FaEye />, permission: 'analytics.view' }, // Reusing analytics permission or specific one?
    { id: 'forms', label: 'Form Submissions', icon: <FaRegClipboard />, permission: 'submissions.view' },
    { id: 'home-content', label: 'Home Content', icon: <FaHome />, permission: 'content.view' },
    { id: 'admins', label: 'Admins & Roles', icon: <MdSecurity />, permission: 'users.manage', superAdminOnly: true },
    { id: 'account', label: 'Account Settings', icon: <FaUserCog />, permission: null }
  ];

  // Filter sections based on permissions
  const sections = allSections.filter(section => {
      if (section.superAdminOnly) return isSuperAdmin();
      if (!section.permission) return true;
      return can(section.permission);
  });

  const renderContent = () => {
    switch (activeSection) {
      case 'analytics':
        return can('analytics.view') ? <Analytics /> : <AccessDenied />;
      case 'projects':
        return can('projects.view') ? <Projects /> : <AccessDenied />;
      case 'team':
        return can('team_performance.view') ? <TeamPerformance /> : <AccessDenied />;
      case 'invoices':
        return can('invoices.view') ? <Invoices /> : <AccessDenied />;
      case 'website-content':
        return can('content.view') ? <WebsiteContent /> : <AccessDenied />;
      case 'visits':
        return can('analytics.view') ? <VisitsTable /> : <AccessDenied />;
      case 'forms':
        return can('submissions.view') ? <Forms /> : <AccessDenied />;
      case 'home-content':
        return can('content.view') ? <HomeContent /> : <AccessDenied />;
      case 'admins':
        return isSuperAdmin() ? <AdminManagement /> : <AccessDenied />;
      case 'account':
        return <AccountSettings />;
      default:
        return (
          <div className="space-y-8">
            {/* Welcome Banner */}
            <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 rounded-3xl shadow-2xl p-8 md:p-12 animate-scaleIn">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl" />
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/20 rounded-full -ml-48 -mb-48 blur-3xl" />
              <div className="relative z-10">
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Welcome to Admin Dashboard
                </h1>
                <p className="text-blue-100 text-lg mb-6">
                  Manage your platform efficiently with powerful tools at your fingertips
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                    <span className="text-white font-medium">🚀 Quick Access</span>
                  </div>
                  <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                    <span className="text-white font-medium">📊 Real-time Data</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Quick Access Cards */}
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full" />
                Quick Access Modules
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sections.filter(s => s.id !== 'dashboard' && s.id !== 'account').map((section, index) => (
                  <div 
                    key={section.id}
                    className="group relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden border border-slate-200/50 animate-slideUp"
                    onClick={() => setActiveSection(section.id)}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-indigo-600/0 group-hover:from-blue-500/10 group-hover:to-indigo-600/10 transition-all duration-500" />
                    
                    {/* Content */}
                    <div className="relative p-6 flex items-center gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                        <span className="text-2xl text-white">{section.icon}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                          {section.label}
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">Click to access</p>
                      </div>
                      <div className="text-slate-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all duration-300">
                        →
                      </div>
                    </div>
                    
                    {/* Bottom accent */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-72 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white flex-col shadow-2xl animate-slideInLeft">
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <MdDashboard className="text-xl" />
            </div>
            <div>
              <h2 className="text-lg font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                Admin Panel
              </h2>
              <p className="text-xs text-slate-400">Management System</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
          {sections.map((section, index) => (
            <button
              key={section.id}
              className={`w-full px-5 py-3.5 flex items-center gap-3 transition-all duration-300 group relative overflow-hidden ${
                activeSection === section.id
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                  : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
              }`}
              onClick={() => setActiveSection(section.id)}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {activeSection === section.id && (
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-indigo-400/20 animate-shimmer" 
                     style={{ backgroundSize: '1000px 100%' }} />
              )}
              <span className={`text-xl transition-transform duration-300 ${
                activeSection === section.id ? 'scale-110' : 'group-hover:scale-110'
              }`}>
                {section.icon}
              </span>
              <span className="font-medium relative z-10">{section.label}</span>
              {activeSection === section.id && (
                <div className="absolute right-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-400 to-indigo-400" />
              )}
            </button>
          ))}
        </nav>
        
        <div className="p-5 border-t border-slate-700/50">
          <button 
            className="w-full px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-xl flex items-center justify-center gap-2 transition-all duration-300 shadow-lg hover:shadow-red-500/50 hover:scale-105 group"
            onClick={handleLogout}
          >
            <FiLogOut className="transition-transform duration-300 group-hover:rotate-12" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col w-full">
        <header className="bg-white/80 backdrop-blur-xl shadow-lg px-4 md:px-8 py-4 md:py-6 border-b border-slate-200/50 animate-slideUp sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg lg:hidden">
                {sections.find(s => s.id === activeSection)?.icon}
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                  {sections.find(s => s.id === activeSection)?.label || 'Dashboard'}
                </h1>
                <p className="text-xs text-slate-500 mt-0.5">Welcome back, Admin</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200/50">
                <span className="text-sm font-medium text-slate-700">
                  {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
              </div>
            </div>
          </div>
        </header>
        
        {/* Mobile Navigation */}
        <div className="lg:hidden bg-gradient-to-r from-slate-900 to-slate-800 text-white p-4 shadow-xl animate-slideUp">
          <select
            value={activeSection}
            onChange={(e) => setActiveSection(e.target.value)}
            className="w-full bg-slate-800/50 backdrop-blur-sm text-white px-4 py-3 rounded-xl border border-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          >
            {sections.map(section => (
              <option key={section.id} value={section.id}>
                {section.label}
              </option>
            ))}
          </select>
          <button 
            className="w-full mt-3 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-xl flex items-center justify-center gap-2 transition-all duration-300 shadow-lg"
            onClick={handleLogout}
          >
            <FiLogOut />
            <span className="font-medium">Logout</span>
          </button>
        </div>
        
        <div className="flex-1 p-4 md:p-8 overflow-auto">
          <div className="animate-fadeIn">
            {renderContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

const AccessDenied = () => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] animate-scaleIn">
        <div className="relative">
            <div className="absolute inset-0 bg-red-500/20 blur-3xl rounded-full animate-pulse" />
            <div className="relative w-32 h-32 bg-gradient-to-br from-red-500 to-red-700 rounded-3xl flex items-center justify-center shadow-2xl">
                <MdLock className="text-6xl text-white" />
            </div>
        </div>
        <h3 className="text-2xl font-bold text-slate-800 mt-8 mb-2">Access Denied</h3>
        <p className="text-slate-500 text-center max-w-md">
            You do not have permission to view this section. Please contact your administrator for access.
        </p>
        <div className="mt-6 px-6 py-3 bg-red-50 border border-red-200 rounded-xl">
            <span className="text-red-700 text-sm font-medium">🔒 Restricted Area</span>
        </div>
    </div>
);

// Account Settings Component
const AccountSettings = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newEmail: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    // Validation
    if (!formData.currentPassword) {
      setError('Current password is required');
      setLoading(false);
      return;
    }

    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (!formData.newEmail && !formData.newPassword) {
      setError('Please provide either a new email or new password');
      setLoading(false);
      return;
    }

    try {
      const updateData = {
        currentPassword: formData.currentPassword,
        ...(formData.newEmail && { email: formData.newEmail }),
        ...(formData.newPassword && { password: formData.newPassword })
      };

      const response = await axios.put(`${SERVER}/admin`, updateData, {
        withCredentials: true
      });

      if (response.status === 200) {
        setMessage('Account updated successfully!');
        setFormData({
          currentPassword: '',
          newEmail: '',
          newPassword: '',
          confirmPassword: ''
        });

        // Update stored email if it was changed
        if (formData.newEmail) {
          localStorage.setItem('adminEmail', formData.newEmail);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto animate-slideUp">
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border border-slate-200/50">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <FaUserCog className="text-3xl text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">Account Settings</h2>
              <p className="text-blue-100">Update your email address or password</p>
            </div>
          </div>
        </div>

        <div className="p-8">
          {message && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-800 px-6 py-4 rounded-2xl mb-6 flex items-center gap-3 animate-slideUp">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-lg">✓</span>
              </div>
              <span className="font-medium">{message}</span>
            </div>
          )}

          {error && (
            <div className="bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 text-red-800 px-6 py-4 rounded-2xl mb-6 flex items-center gap-3 animate-slideUp">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-lg">✕</span>
              </div>
              <span className="font-medium">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="currentPassword" className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <MdLock className="text-blue-600" />
                Current Password *
              </label>
              <div className="relative">
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  placeholder="Enter your current password"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3.5 pl-12 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-500 transition-all"
                />
                <MdLock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="newEmail" className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <MdEmail className="text-blue-600" />
                New Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="newEmail"
                  name="newEmail"
                  value={formData.newEmail}
                  onChange={handleChange}
                  placeholder="Enter new email address"
                  disabled={loading}
                  className="w-full px-4 py-3.5 pl-12 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-500 transition-all"
                />
                <MdEmail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="newPassword" className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <MdLock className="text-blue-600" />
                  New Password
                </label>
                <div className="relative">
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Enter new password"
                    disabled={loading}
                    className="w-full px-4 py-3.5 pl-12 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-500 transition-all"
                  />
                  <MdLock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <MdLock className="text-blue-600" />
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm new password"
                    disabled={loading}
                    className="w-full px-4 py-3.5 pl-12 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-500 transition-all"
                  />
                  <MdLock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 disabled:from-slate-400 disabled:to-slate-500 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center justify-center gap-2 group"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <span>Update Account</span>
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;